import { useId } from 'react';
import '../../styles/gbr-cur.css';

/** Currency icons (Bookmarks = ribbon bookmark, Golden Quills = gold feather). Same art as /icons/*.svg. */
type IcoProps = { size?: number | string; className?: string; title?: string };

export function BookmarkIco({ size = '1.1em', className, title }: IcoProps) {
  const g = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={`gbr-cur ${className || ''}`} role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} aria-label={title}>
      <defs>
        <linearGradient id={`bm${g}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--bm-a, #19b3aa)' }} />
          <stop offset="1" style={{ stopColor: 'var(--bm-b, #08605d)' }} />
        </linearGradient>
      </defs>
      <path d="M6.2 2h11.6A1.7 1.7 0 0 1 19.5 3.7v17.4a.9.9 0 0 1-1.42.74L12 17.5l-6.08 4.34A.9.9 0 0 1 4.5 21.1V3.7A1.7 1.7 0 0 1 6.2 2z" fill={`url(#bm${g})`} />
      <path d="M6.2 2H8v17.7l-2.08 1.48A.9.9 0 0 1 4.5 20.5V3.7A1.7 1.7 0 0 1 6.2 2z" style={{ fill: 'var(--bm-c, #064e4b)' }} opacity=".55" />
      <path d="M12 6.2l1.25 2.55 2.8.4-2.03 1.98.48 2.79L12 12.6l-2.5 1.32.48-2.79-2.03-1.98 2.8-.4z" fill="#ffd36b" />
    </svg>
  );
}

export function QuillIco({ size = '1.1em', className, title }: IcoProps) {
  const g = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={`gbr-cur ${className || ''}`} role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} aria-label={title}>
      <defs>
        <linearGradient id={`qa${g}`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe7a3" />
          <stop offset="1" stopColor="#ffb23f" />
        </linearGradient>
        <linearGradient id={`qb${g}`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffc24f" />
          <stop offset="1" stopColor="#d1850a" />
        </linearGradient>
      </defs>
      <path d="M22 1.8C18.6 2.1 15.6 3.3 13.2 5.1l1.3.5-2.5.8C9.8 8.5 8.2 10.9 7.5 13.6l-.3 3.2Q13 10 22 1.8z" fill={`url(#qa${g})`} />
      <path d="M22 1.8Q13 10 7.2 16.8l3.3-.3c1.9-.5 3.4-1.2 4.7-2l-.9-.9 2.2-.3C19.6 10.6 21.6 6.6 22 1.8z" fill={`url(#qb${g})`} />
      <path d="M21.4 2.5Q13 10.2 4.4 19.6" style={{ stroke: 'var(--q-shaft, #7a4c00)' }} strokeWidth="1.15" strokeLinecap="round" fill="none" />
      <path d="M5.2 18.9L2.3 21.7l3.2-2.2z" fill="#5c3a00" stroke="#5c3a00" strokeWidth=".9" strokeLinejoin="round" />
    </svg>
  );
}

/** Amount followed by its currency icon, e.g. "1,250 [bookmark]". Screen readers hear "1,250 Bookmarks".
 *  compact: from 1,000,000 phones (<=480 px) see "1.2M"; the full number stays for screen readers and in the tooltip. */
export function Amt({ n, kind, sign, className, compact }: { n?: number | string; kind: 'bm' | 'gq'; sign?: boolean; className?: string; compact?: boolean }) {
  const v = typeof n === 'number' ? n : Number(n) || 0;
  const label = kind === 'bm' ? (v === 1 ? 'Bookmark' : 'Bookmarks') : (v === 1 ? 'Golden Quill' : 'Golden Quills');
  return (
    <span className={`gbr-amt ${className || ''}`}>
      {compact && Math.abs(v) >= 1e6 ? (
        <span className="gbr-amt-n" title={v.toLocaleString('en-US')}>
          <span className="gbr-amt-f">{sign && v > 0 ? '+' : ''}{v.toLocaleString('en-US')}</span>
          <span className="gbr-amt-c" aria-hidden="true">{sign && v > 0 ? '+' : ''}{new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}</span>
        </span>
      ) : (
        <>{sign && v > 0 ? '+' : ''}{v.toLocaleString('en-US')}</>
      )}
      {kind === 'bm' ? <BookmarkIco /> : <QuillIco />}
      <span className="sr-only"> {label}</span>
    </span>
  );
}
